import debounce from "lodash.debounce";
import EventClass from "common/utils/ui/event-class";
import { formatCurrency } from "common/utils/format/number";
import DesignUploadCtaPlpCookieClass from "components/jiffy/design-upload-cta/design-upload-cta-plp/design-upload-cta-plp-cookie-class";
import ProductCardMadeCanvasClass from "../made-canvas/made-canvas-class";

const ACTIVE_SWATCH_CLASS = "js-color-swatch-active";
const ACTIVE_SWATCH_RING_CLASSES = ["ring-2", "ring-offset-1", "ring-blue-600"];
const IMAGE_UPDATE_DELAY_MS = 350;

export default class ColorSwatchHoverClass extends EventClass {
  #$target;

  get #$productPreview() {
    return this.#$productCard?.querySelector(".js-product-image");
  }

  get #$productCard() {
    return this.#$target.closest(".js-product-card-v3");
  }

  get #$colorNameDisplay() {
    return this.#$productCard?.querySelector(".js-color-name-display");
  }

  get #$bulkPriceDisplay() {
    return this.#$productCard?.querySelector(".js-bulk-price");
  }

  get #$retailPriceDisplay() {
    return this.#$productCard?.querySelector(".js-retail-price");
  }

  get #$productCardLink() {
    return this.#$productCard?.querySelector(".js-product-card-link");
  }

  get #$bulkPriceTableBody() {
    return this.#$productCard?.querySelector(".js-bulk-price-table-body");
  }

  get #$bulkPriceTableRows() {
    return this.#$bulkPriceTableBody?.querySelectorAll(".js-bulk-price-table-row");
  }

  get #$colorSwatchesContainer() {
    return this.#$productCard?.querySelector(".js-color-swatches-container");
  }

  #debouncedUpdateProductImage = debounce((imgSrc, printableArea, frontZoomedImageUrl) => {
    this.#updateProductImage(imgSrc, printableArea, frontZoomedImageUrl);
  }, IMAGE_UPDATE_DELAY_MS);

  event(event, $target) {
    super.event(event, $target);

    this.#$target = $target;
    const {
      imgSrc,
      printableArea,
      frontZoomedImageUrl,
      color,
      price,
      tierZeroPrice,
      bulkPrices,
      wholesaleBulkPrices,
      productUrl
    } = this.#$target.dataset;

    this.#handleImageUpdate(event.type, { imgSrc, printableArea, frontZoomedImageUrl });
    this.#updateColorName(color);
    this.#updatePriceDisplay(price, tierZeroPrice);
    this.#updateBulkPriceTable(bulkPrices, wholesaleBulkPrices);
    this.#updateActiveSwatchState();
    this.#updateProductCardUrl(productUrl);
  }

  #handleImageUpdate(eventType, imageData) {
    const { imgSrc, printableArea, frontZoomedImageUrl } = imageData;

    this.#debouncedUpdateProductImage.cancel();

    if (eventType === "mouseenter") {
      this.#debouncedUpdateProductImage(imgSrc, printableArea, frontZoomedImageUrl);
      return;
    }

    this.#updateProductImage(imgSrc, printableArea, frontZoomedImageUrl);
  }

  #updateProductImage(imgSrc, printableArea, frontZoomedImageUrl) {
    if (!this.#$productPreview) return;

    const url = DesignUploadCtaPlpCookieClass.previewUrl;
    if (url && frontZoomedImageUrl && printableArea) {
      this.#$productPreview.src = frontZoomedImageUrl;
      this.#$productPreview.dataset.printableArea = printableArea;
      this.#$productPreview.dataset.frontZoomedImageUrl = frontZoomedImageUrl;
      new ProductCardMadeCanvasClass({ url }).initSingle(this.#$productPreview);
    } else {
      this.#$productPreview.src = imgSrc;
    }
  }

  #updateColorName(colorName) {
    if (!this.#$colorNameDisplay || !colorName) return;
    this.#$colorNameDisplay.textContent = colorName;
  }

  #updatePriceDisplay(price, tierZeroPrice) {
    if (price && this.#$bulkPriceDisplay) {
      this.#$bulkPriceDisplay.textContent = formatCurrency(parseFloat(price));
    }

    if (tierZeroPrice && this.#$retailPriceDisplay) {
      this.#$retailPriceDisplay.textContent = formatCurrency(parseFloat(tierZeroPrice));
    }
  }

  #updateActiveSwatchState() {
    if (!this.#$colorSwatchesContainer) return;

    // Remove active state from all swatches
    const $allSwatches = this.#$colorSwatchesContainer.querySelectorAll(".js-color-swatches-link");
    $allSwatches.forEach($swatch => {
      $swatch.classList.remove(ACTIVE_SWATCH_CLASS, ...ACTIVE_SWATCH_RING_CLASSES);
    });

    // Add active state to the current swatch
    this.#$target.classList.add(ACTIVE_SWATCH_CLASS, ...ACTIVE_SWATCH_RING_CLASSES);
  }

  #updateProductCardUrl(productUrl) {
    if (!productUrl) return;

    const { color } = this.#$target.dataset;

    // Update the main product card link's destination and accessible name. The
    // aria-label must track the selected color or screen readers announce the
    // initially rendered color while the link points at the new one.
    if (this.#$productCardLink) {
      this.#$productCardLink.href = productUrl;

      // Inside this block #$productCardLink is truthy, which the getter derives
      // from #$productCard, so the card is guaranteed present here. Fall back to
      // the product name alone when a color is absent so the label is never left
      // describing a stale, previously selected color.
      const { productName } = this.#$productCard.dataset;
      if (productName) {
        this.#$productCardLink.setAttribute(
          "aria-label",
          color ? `${productName} - ${color}` : productName
        );
      }
    }

    // Update data-product-url on the product card itself
    if (this.#$productCard) {
      this.#$productCard.dataset.productUrl = productUrl;
      this.#$productCard.dataset.color = color;
      this.#$productCard.dataset.compositionId = this.#$target.dataset.compositionId;
    }
  }

  #updateBulkPriceTable(bulkPricesJson, wholesaleBulkPricesJson) {
    if (!bulkPricesJson || !this.#$bulkPriceTableRows) return;

    try {
      const bulkPrices = JSON.parse(bulkPricesJson);
      if (!bulkPrices || typeof bulkPrices !== "object") return;

      const wholesaleBulkPrices =
        wholesaleBulkPricesJson && wholesaleBulkPricesJson !== "null"
          ? JSON.parse(wholesaleBulkPricesJson)
          : null;

      // Update each row in the table with the new prices
      this.#$bulkPriceTableRows.forEach($row => {
        const $cells = $row.querySelectorAll(".js-bulk-price-table-cell");
        if ($cells.length >= 2) {
          const quantity = $cells[0].textContent.trim();

          // Update regular price (2nd column)
          const price = bulkPrices[quantity];
          if (price !== undefined) {
            $cells[1].textContent = formatCurrency(parseFloat(price));
          }

          // Update wholesale price (3rd column) if it exists
          if ($cells.length >= 3 && wholesaleBulkPrices) {
            const wholesalePrice = wholesaleBulkPrices[quantity];
            if (wholesalePrice !== undefined) {
              $cells[2].textContent = formatCurrency(parseFloat(wholesalePrice));
            }
          }
        }
      });
    } catch {
      // Silently fail if JSON parsing fails
    }
  }
}
